import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { QRCodeComponent } from "angularx-qrcode";
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  map,
  Subject,
  Subscription,
  switchMap,
  tap,
} from "rxjs";
import { KioskSetupService } from "../../services/kiosk-setup.service";
import {
  KioskLocalStorageKeysEnum,
  LocalStorageService,
} from "../../services/local-storage.service";
import {
  AuthTokenResponse,
  ProvisioningResponse,
  ProvisioningResponseTypeEnum,
} from "../../models/kiosk.model";
import { environment } from "../../../environments/environment";
import { LedActivationService } from "../../services/led-activation.service";
import { WakeLockService } from "../../services/wake-lock.service";

interface ProvisioningError {
  error: {
    errorCode: string;
  };
}

@Component({
  selector: "db-qr-generator",
  standalone: true,
  imports: [CommonModule, QRCodeComponent],
  templateUrl: "./qr-generator.component.html",
  styleUrls: ["./qr-generator.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QRGeneratorComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly kioskSetupService = inject(KioskSetupService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly ledActivationService = inject(LedActivationService);
  private readonly wakeLockService = inject(WakeLockService);

  private readonly POLLING_INTERVAL = 4000; // 4 seconds
  private readonly COUNTDOWN_MINUTES = 10;

  readonly ProvisioningResponseTypeEnum = ProvisioningResponseTypeEnum;

  private readonly stopPolling$ = new Subject<void>();
  private timers = {
    countdown: undefined as number | undefined,
    polling: undefined as number | undefined,
  };
  private subscriptions = new Subscription();
  private sessionId = "";

  protected readonly qrData$ = this.route.queryParams.pipe(
    map((params: Params) => {
      this.sessionId = (params["shortId"] as string) ?? "";
      const androidUserAgentString = window.navigator.userAgent.slice(
        window.navigator.userAgent.indexOf("Android"),
      );
      const androidDeviceName = encodeURIComponent(
        androidUserAgentString.slice(
          androidUserAgentString.indexOf("; ") + 1,
          androidUserAgentString.indexOf(")"),
        ),
      );
      return `${environment.qrCodeDeepLink}${this.sessionId}&deviceId=${androidDeviceName || "Device"}`;
    }),
  );

  protected readonly countdown$ = new BehaviorSubject<string>(
    this.formatTime(this.COUNTDOWN_MINUTES * 60),
  );
  private roomId = "";

  ngOnInit(): void {
    // Start countdown and polling
    this.startCountdown();
    this.startPolling();
    this.ledActivationService.setLedSetupInProgressStatus();
  }

  private startPolling(): void {
    const pollSession = () => {
      if (!this.sessionId) return;

      this.subscriptions.add(
        this.kioskSetupService
          .getProvisioningSession(this.sessionId)
          .pipe(
            // Validate and process provisioning session
            map(({ refreshToken, type, roomId }: ProvisioningResponse) => {
              if (type !== ProvisioningResponseTypeEnum.KIOSK_ROOM) {
                return null;
              }

              // Store refresh token and room ID
              this.localStorageService.setItem(
                KioskLocalStorageKeysEnum.REFRESH_TOKEN,
                refreshToken,
              );
              this.localStorageService.setItem(
                KioskLocalStorageKeysEnum.ROOM_ID,
                roomId ?? "",
              );
              this.roomId = roomId ?? "";
              this.sessionId = "";
              return { refreshToken };
            }),

            // Get auth token if session is valid
            switchMap((session) =>
              session ? this.kioskSetupService.getAuthToken() : EMPTY,
            ),

            // Handle errors
            catchError((error: ProvisioningError) => {
              if (error?.error?.errorCode === "session_not_configured") {
                return EMPTY;
              }

              console.error("Error during provisioning:", error);
              void this.router.navigate(["/setup"]);
              return EMPTY;
            }),

            // Process successful auth token response
            tap(({ token }: AuthTokenResponse) => {
              this.localStorageService.setItem(
                KioskLocalStorageKeysEnum.ID_TOKEN,
                token,
              );

              if (this.roomId) {
                void this.router.navigate([`/rooms/${this.roomId}`]);
              }
            }),
          )
          .subscribe(),
      );
    };

    // Initial check and start polling
    pollSession();
    this.timers.polling = window.setInterval(
      pollSession,
      this.POLLING_INTERVAL,
    );
  }

  private startCountdown(): void {
    let remainingSeconds = this.COUNTDOWN_MINUTES * 60;

    this.timers.countdown = window.setInterval(() => {
      remainingSeconds--;

      if (remainingSeconds <= 0) {
        this.cleanup();
        void this.router.navigate(["/setup"]);
        return;
      }

      this.countdown$.next(this.formatTime(remainingSeconds));
    }, 1000);
  }

  private formatTime(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }

  private cleanup(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();

    if (this.timers.countdown) {
      window.clearInterval(this.timers.countdown);
    }

    if (this.timers.polling) {
      window.clearInterval(this.timers.polling);
    }

    this.subscriptions.unsubscribe();
  }

  navigateToSetup(): void {
    void this.router.navigate(["/setup"]);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
